/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Alert, Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { currencyFormatter } from "../../constants";
import callApiJson from "../../api/callApi";

interface EpcField {
  sfFieldName: string;
  label: string;
}

const SOLAR_SALES_FIELDS: EpcField[] = [
  { sfFieldName: "pv_installer_cost_nom__c", label: "PV Installer Cost Nom" },
  { sfFieldName: "pv_sunrock_procurement__c", label: "PV Sunrock Procurement" },
];

const BESS_SALES_FIELDS: EpcField[] = [
  {
    sfFieldName: "bess_installer_cost_nom__c",
    label: "BESS Installer Cost Nom",
  },
  {
    sfFieldName: "bess_sunrock_procurement__c",
    label: "BESS Sunrock Procurement",
  },
];

const SHARED_SALES_FIELDS: EpcField[] = [
  {
    sfFieldName: "third_party_dev_cost_nom__c",
    label: "Project Development Cost $ Nominal",
  },
];

const SOLAR_ENG_EXTRA_FIELDS: EpcField[] = [
  {
    sfFieldName: "pv_additional_epc_cost_nom__c",
    label: "PV Additional EPC Cost Nom",
  },
];

const BESS_ENG_EXTRA_FIELDS: EpcField[] = [
  {
    sfFieldName: "bess_additional_epc_cost_nom__c",
    label: "BESS Additional EPC Cost Nom",
  },
];

const SHARED_ENG_EXTRA_FIELDS: EpcField[] = [
  { sfFieldName: "sr_dev_cost_nom__c", label: "Sunrock Dev Cost Nom" },
];

const ALL_POSSIBLE_FIELDS = [
  ...SOLAR_SALES_FIELDS,
  ...BESS_SALES_FIELDS,
  ...SHARED_SALES_FIELDS,
  ...SOLAR_ENG_EXTRA_FIELDS,
  ...BESS_ENG_EXTRA_FIELDS,
  ...SHARED_ENG_EXTRA_FIELDS,
];

function buildInitialValues(systemDesignData: any): Record<string, string> {
  const result: Record<string, string> = {};
  ALL_POSSIBLE_FIELDS.forEach((f) => {
    const raw = systemDesignData?.[f.sfFieldName];
    result[f.sfFieldName] =
      raw !== undefined && raw !== null && raw !== "" ? String(raw) : "0";
  });
  return result;
}

function NewSystemDesignModal({
  open,
  onClose,
  systemDesignData,
  optimizedEpcTotal,
  isSDSales,
  onSdCreated,
}: {
  open: boolean;
  onClose: () => void;
  systemDesignData: any;
  optimizedEpcTotal: number;
  isSDSales: boolean;
  onSdCreated: (sd: { id: string; name: string }) => Promise<void>;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [createdSd, setCreatedSd] = useState<{ id: string; name: string } | null>(null);
  const [rerunStatus, setRerunStatus] = useState<"running" | "failed">("running");
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() =>
    buildInitialValues(systemDesignData),
  );

  // Reset state each time the modal opens
  useEffect(() => {
    if (open) {
      setFieldValues(buildInitialValues(systemDesignData));
      setCreatedSd(null);
      setRerunStatus("running");
    }
  }, [open, systemDesignData]);

  const projectType: string = systemDesignData?.project_type__c || "Solar-only";
  const pvSizeKw: number =
    parseFloat(systemDesignData?.pv_size_dc_kw_sd__c) || 0;
  const bessKw: number =
    parseFloat(systemDesignData?.battery_pwr_rating_kw__c) || 0;
  const bessKwh: number =
    parseFloat(systemDesignData?.battery_energy_rating_kwh__c) || 0;

  const epcFields = useMemo<EpcField[]>(() => {
    const hasSolar = projectType !== "Storage-only";
    const hasBess = projectType !== "Solar-only";
    const fields: EpcField[] = [];
    if (hasSolar) fields.push(...SOLAR_SALES_FIELDS);
    if (hasBess) fields.push(...BESS_SALES_FIELDS);
    fields.push(...SHARED_SALES_FIELDS);
    if (!isSDSales) {
      if (hasSolar) fields.push(...SOLAR_ENG_EXTRA_FIELDS);
      if (hasBess) fields.push(...BESS_ENG_EXTRA_FIELDS);
      fields.push(...SHARED_ENG_EXTRA_FIELDS);
    }
    return fields;
  }, [projectType, isSDSales]);

  const currentTotal = useMemo(
    () =>
      epcFields.reduce(
        (sum, f) => sum + (parseFloat(fieldValues[f.sfFieldName] || "0") || 0),
        0,
      ),
    [epcFields, fieldValues],
  );

  // Per-watt display: solar uses pv size, storage-only uses battery power rating
  const epcPerWattSizeKw = projectType === "Storage-only" ? bessKw : pvSizeKw;
  const epcPerWatt =
    epcPerWattSizeKw > 0 ? currentTotal / (epcPerWattSizeKw * 1000) : null;

  // Per-kWh display (BESS): derived from BESS fields only
  const bessActiveFields = [
    ...BESS_SALES_FIELDS,
    ...BESS_ENG_EXTRA_FIELDS,
  ].filter((f) => epcFields.some((ef) => ef.sfFieldName === f.sfFieldName));
  const bessSubtotal = bessActiveFields.reduce(
    (sum, f) => sum + (parseFloat(fieldValues[f.sfFieldName] || "0") || 0),
    0,
  );
  const epcPerKwh =
    bessKwh > 0 && projectType !== "Solar-only" ? bessSubtotal / bessKwh : null;

  const totalsMatch = Math.abs(currentTotal - optimizedEpcTotal) < 0.01;

  const epcNotPositive =
    projectType !== "Storage-only" && epcPerWatt !== null && epcPerWatt <= 0;

  const handleFieldChange = (sfFieldName: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [sfFieldName]: value }));
  };

  const handleCreate = async () => {
    if (epcNotPositive) {
      toast.error(
        "The entered total EPC cost must be greater than $0.00/watt.",
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }

    setIsCreating(true);
    try {
      const epcFieldsPayload: Record<string, number> = {};
      epcFields.forEach((f) => {
        epcFieldsPayload[f.sfFieldName] =
          parseFloat(fieldValues[f.sfFieldName] || "0") || 0;
      });

      if (epcPerWattSizeKw > 0) {
        epcFieldsPayload["epc_total_per_watt_sd__c"] =
          currentTotal / (epcPerWattSizeKw * 1000);
      }

      if (bessKwh > 0) {
        epcFieldsPayload["epc_bess_per_kwh_sd__c"] = bessSubtotal / bessKwh;
      }

      // callApiJson redirects to /login on 401 (session timeout) instead of
      // trying to parse the HTML error page as JSON
      const { id: newSdId, name: newSdName } = await callApiJson(
        "/api/system-designs/clone",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            system_design_id: systemDesignData.Id,
            epc_fields: epcFieldsPayload,
            is_sales_design: isSDSales,
          }),
        },
      );
      toast.success("New System Design created successfully", {
        position: toast.POSITION.TOP_RIGHT,
      });
      const newSd = { id: newSdId, name: newSdName };
      setCreatedSd(newSd);
      setIsCreating(false);
      await rerunDevFee(newSd);
    } catch (err: any) {
      // callApi throws the raw response body; surface the API's `error` field
      // when the body is JSON
      let message = err.message;
      try {
        message = JSON.parse(message).error || message;
      } catch {
        // not JSON, keep the raw message
      }
      toast.error("Failed to create system design: " + message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Re-run the Dev Fee solver on the new SD: its Solar / BESS cost split may
  // differ, which changes ITC basis, incentives and the dev fee.
  const rerunDevFee = async (sd: { id: string; name: string }) => {
    setRerunStatus("running");
    try {
      await onSdCreated(sd);
      // Close so the user sees the updated outputs
      onClose();
    } catch (err: any) {
      console.error("Dev Fee solver re-run failed:", err);
      setRerunStatus("failed");
    }
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      size="lg"
      aria-labelledby="new-sd-modal-title"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="new-sd-modal-title">New System Design</Modal.Title>
      </Modal.Header>
      {createdSd && rerunStatus === "running" ? (
        <Modal.Body>
          <Typography>
            System Design <strong>{createdSd.name}</strong> created. Re-running
            the Dev Fee solver on the new System Design...
          </Typography>
        </Modal.Body>
      ) : createdSd && rerunStatus === "failed" ? (
        <>
          <Modal.Body>
            <Alert severity="error">
              System Design <strong>{createdSd.name}</strong> was created, but
              the Dev Fee solver failed to run on it, so the outputs still
              reflect the previous System Design. Retry, or close and select
              the new System Design to re-run the quote.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="contained"
              style={{ marginLeft: "10px", color: "white" }}
              onClick={() => rerunDevFee(createdSd)}
            >
              Retry
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body>
            {/* EPC per watt / per kWh summary */}
            <Box
              sx={{
                display: "flex",
                gap: 4,
                mb: 2,
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              {epcPerWatt !== null && (
                <Typography variant="body2">
                  <strong>EPC ($/W):</strong> {currencyFormatter(epcPerWatt)}
                </Typography>
              )}
              {epcPerKwh !== null && (
                <Typography variant="body2">
                  <strong>EPC BESS ($/kWh):</strong> {currencyFormatter(epcPerKwh)}
                </Typography>
              )}
            </Box>

            {/* Totals comparison */}
            <Box sx={{ display: "flex", gap: 4, mb: 2 }}>
              <Typography variant="body2">
                Optimized EPC Total:{" "}
                <strong>{currencyFormatter(optimizedEpcTotal)}</strong>
              </Typography>
              <Typography variant="body2">
                Current Total:{" "}
                <strong style={{ color: totalsMatch ? "green" : undefined }}>
                  {currencyFormatter(currentTotal)}
                </strong>
              </Typography>
            </Box>

            {!totalsMatch && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Adjust the values below until Current Total matches Optimized
                EPC Total <strong>(difference:{" "}
                {currencyFormatter(Math.abs(currentTotal - optimizedEpcTotal))}
                )</strong>.
              </Alert>
            )}

            {epcNotPositive && (
              <Alert severity="error" sx={{ mb: 2 }}>
                The total EPC cost must be greater than $0.00/W.
              </Alert>
            )}

            {/* EPC cost input fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {epcFields.map((f) => (
                <TextField
                  key={f.sfFieldName}
                  label={f.label}
                  type="text"
                  inputMode="decimal"
                  value={(() => {
                    const raw = fieldValues[f.sfFieldName];
                    const parts = raw.split(".");
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    return parts.join(".");
                  })()}
                  onChange={(e) =>
                    handleFieldChange(
                      f.sfFieldName,
                      e.target.value.replace(/,/g, ""),
                    )
                  }
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              ))}
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outlined" onClick={onClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!totalsMatch || epcNotPositive || isCreating}
              style={{ marginLeft: "10px", color: "white" }}
              onClick={handleCreate}
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default NewSystemDesignModal;
